import { useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import styles from "./WorkflowCollectAddressVerification.module.scss";

export interface AddressFormData {
  street: string;
  municipality: string;
  subdivisionIso2: string;
  postalCode: string;
}

interface Props {
  title: string;
  onSubmit: (address: AddressFormData) => void;
  onNoUsAddress: () => void;
  isSubmitting: boolean;
}

const US_STATES: { value: string; label: string }[] = [
  { value: "US-AL", label: "Alabama" },
  { value: "US-AK", label: "Alaska" },
  { value: "US-AZ", label: "Arizona" },
  { value: "US-AR", label: "Arkansas" },
  { value: "US-CA", label: "California" },
  { value: "US-CO", label: "Colorado" },
  { value: "US-CT", label: "Connecticut" },
  { value: "US-DC", label: "District of Columbia" },
  { value: "US-DE", label: "Delaware" },
  { value: "US-FL", label: "Florida" },
  { value: "US-GA", label: "Georgia" },
  { value: "US-HI", label: "Hawaii" },
  { value: "US-ID", label: "Idaho" },
  { value: "US-IL", label: "Illinois" },
  { value: "US-IN", label: "Indiana" },
  { value: "US-IA", label: "Iowa" },
  { value: "US-KS", label: "Kansas" },
  { value: "US-KY", label: "Kentucky" },
  { value: "US-LA", label: "Louisiana" },
  { value: "US-ME", label: "Maine" },
  { value: "US-MD", label: "Maryland" },
  { value: "US-MA", label: "Massachusetts" },
  { value: "US-MI", label: "Michigan" },
  { value: "US-MN", label: "Minnesota" },
  { value: "US-MS", label: "Mississippi" },
  { value: "US-MO", label: "Missouri" },
  { value: "US-MT", label: "Montana" },
  { value: "US-NE", label: "Nebraska" },
  { value: "US-NV", label: "Nevada" },
  { value: "US-NH", label: "New Hampshire" },
  { value: "US-NJ", label: "New Jersey" },
  { value: "US-NM", label: "New Mexico" },
  { value: "US-NY", label: "New York" },
  { value: "US-NC", label: "North Carolina" },
  { value: "US-ND", label: "North Dakota" },
  { value: "US-OH", label: "Ohio" },
  { value: "US-OK", label: "Oklahoma" },
  { value: "US-OR", label: "Oregon" },
  { value: "US-PA", label: "Pennsylvania" },
  { value: "US-RI", label: "Rhode Island" },
  { value: "US-SC", label: "South Carolina" },
  { value: "US-SD", label: "South Dakota" },
  { value: "US-TN", label: "Tennessee" },
  { value: "US-TX", label: "Texas" },
  { value: "US-UT", label: "Utah" },
  { value: "US-VT", label: "Vermont" },
  { value: "US-VA", label: "Virginia" },
  { value: "US-WA", label: "Washington" },
  { value: "US-WV", label: "West Virginia" },
  { value: "US-WI", label: "Wisconsin" },
  { value: "US-WY", label: "Wyoming" },
];

export function WorkflowCollectAddressScreen({ title, onSubmit, onNoUsAddress, isSubmitting }: Props) {
  const { t } = useTranslation();
  const [street, setStreet] = useState("");
  const [aptSuite, setAptSuite] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [isDirty, setIsDirty] = useState(false);

  const streetRef = useRef<HTMLInputElement>(null);
  const cityRef = useRef<HTMLInputElement>(null);
  const stateRef = useRef<HTMLSelectElement>(null);
  const postalRef = useRef<HTMLInputElement>(null);

  const isValid = street.trim() !== "" && city.trim() !== "" && state !== "" && postalCode.trim() !== "";

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setIsDirty(true);
      if (!isValid) {
        if (street.trim() === "") streetRef.current?.focus();
        else if (city.trim() === "") cityRef.current?.focus();
        else if (state === "") stateRef.current?.focus();
        else if (postalCode.trim() === "") postalRef.current?.focus();
        return;
      }

      const fullStreet = aptSuite.trim() ? `${street.trim()} ${aptSuite.trim()}` : street.trim();
      onSubmit({
        street: fullStreet,
        municipality: city.trim(),
        subdivisionIso2: state,
        postalCode: postalCode.trim(),
      });
    },
    [isValid, street, aptSuite, city, state, postalCode, onSubmit, streetRef, cityRef, stateRef, postalRef]
  );

  const showStreetError = isDirty && street.trim() === "";
  const showCityError = isDirty && city.trim() === "";
  const showStateError = isDirty && state === "";
  const showPostalError = isDirty && postalCode.trim() === "";

  return (
    <div className={styles.addressCard}>
      <h1 className={styles.addressTitle}>{title}</h1>
      <form onSubmit={handleSubmit} noValidate aria-label={title}>
        <div className={styles.formGroup}>
          <label className={styles.fieldLabel} htmlFor="street-input">
            {t("collectAddress.fields.street")}
          </label>
          <input
            ref={streetRef}
            id="street-input"
            type="text"
            className={styles.fieldInput}
            data-error={showStreetError ? "true" : undefined}
            value={street}
            onChange={(e) => setStreet(e.target.value)}
            disabled={isSubmitting}
            autoComplete="address-line1"
            aria-required="true"
            aria-invalid={showStreetError || undefined}
            aria-describedby={showStreetError ? "street-error" : undefined}
          />
          {showStreetError && (
            <p id="street-error" className={styles.fieldError} role="alert">
              {t("collectAddress.errors.streetRequired")}
            </p>
          )}
        </div>

        <div className={styles.formGroup}>
          <label className={styles.fieldLabel} htmlFor="apt-suite-input">
            {t("collectAddress.fields.aptSuite")}
          </label>
          <input
            id="apt-suite-input"
            type="text"
            className={styles.fieldInput}
            value={aptSuite}
            onChange={(e) => setAptSuite(e.target.value)}
            disabled={isSubmitting}
            autoComplete="address-line2"
          />
        </div>

        <div className={styles.formGroup}>
          <label className={styles.fieldLabel} htmlFor="city-input">
            {t("collectAddress.fields.city")}
          </label>
          <input
            ref={cityRef}
            id="city-input"
            type="text"
            className={styles.fieldInput}
            data-error={showCityError ? "true" : undefined}
            value={city}
            onChange={(e) => setCity(e.target.value)}
            disabled={isSubmitting}
            autoComplete="address-level2"
            aria-required="true"
            aria-invalid={showCityError || undefined}
            aria-describedby={showCityError ? "city-error" : undefined}
          />
          {showCityError && (
            <p id="city-error" className={styles.fieldError} role="alert">
              {t("collectAddress.errors.cityRequired")}
            </p>
          )}
        </div>

        <div className={styles.formGroup}>
          <label className={styles.fieldLabel} htmlFor="state-select">
            {t("collectAddress.fields.state")}
          </label>
          <select
            ref={stateRef}
            id="state-select"
            className={styles.fieldSelect}
            data-error={showStateError ? "true" : undefined}
            value={state}
            onChange={(e) => setState(e.target.value)}
            disabled={isSubmitting}
            autoComplete="address-level1"
            aria-required="true"
            aria-invalid={showStateError || undefined}
            aria-describedby={showStateError ? "state-error" : undefined}
          >
            <option value="" disabled>
              {t("collectAddress.fields.statePlaceholder")}
            </option>
            {US_STATES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
          {showStateError && (
            <p id="state-error" className={styles.fieldError} role="alert">
              {t("collectAddress.errors.stateRequired")}
            </p>
          )}
        </div>

        <div className={styles.formGroup}>
          <label className={styles.fieldLabel} htmlFor="postal-code-input">
            {t("collectAddress.fields.postalCode")}
          </label>
          <input
            ref={postalRef}
            id="postal-code-input"
            type="text"
            className={styles.fieldInput}
            data-error={showPostalError ? "true" : undefined}
            value={postalCode}
            onChange={(e) => setPostalCode(e.target.value)}
            disabled={isSubmitting}
            autoComplete="postal-code"
            inputMode="numeric"
            aria-required="true"
            aria-invalid={showPostalError || undefined}
            aria-describedby={showPostalError ? "postal-error" : undefined}
          />
          {showPostalError && (
            <p id="postal-error" className={styles.fieldError} role="alert">
              {t("collectAddress.errors.postalCodeRequired")}
            </p>
          )}
        </div>

        <button
          type="submit"
          className={styles.continueButton}
          disabled={isSubmitting}
        >
          {isSubmitting
            ? t("collectAddress.buttons.submitting")
            : t("collectAddress.buttons.continue")}
        </button>
      </form>

      <button type="button" className={styles.noAddressLink} disabled={isSubmitting} onClick={onNoUsAddress}>
        {t("collectAddress.buttons.noUsAddress")}
      </button>
    </div>
  );
}
